/**
 * InvitePartnerModal - Calm, simple partner invitation modal
 * 
 * ARC Doctrine: Calm language, no urgency, no blame
 */
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Alert, AlertDescription } from "../ui/alert";
import { toast } from "sonner";
import api from "../../lib/api";
import { 
  UserPlus, 
  Mail, 
  Loader2, 
  CheckCircle2,
  Info
} from "lucide-react";

export function InvitePartnerModal({ 
  open, 
  onOpenChange, 
  onInviteSent 
}) {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!email.trim()) {
      setError("Please enter an email address");
      return;
    }
    
    setLoading(true);
    
    try {
      const res = await api.post("/api/household/invite", {
        email: email.trim().toLowerCase(),
        message: message.trim() || null
      });
      
      if (res.ok) {
        setSuccess(true);
        toast.success("Invitation sent");
        onInviteSent?.();
        
        // Reset and close after delay
        setTimeout(() => {
          setEmail("");
          setMessage("");
          setSuccess(false);
          onOpenChange(false);
        }, 2000);
      } else {
        // Clone the response before reading to avoid "body stream already read" error
        try {
          const data = await res.clone().json();
          setError(data.detail || "Could not send invitation");
        } catch {
          // If JSON parsing fails, use status-based message
          if (res.status === 400) {
            setError("There is already a pending invite for this household");
          } else {
            setError("Could not send invitation");
          }
        }
      }
    } catch (err) {
      console.error("Invite error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setEmail("");
      setMessage("");
      setError(null);
      setSuccess(false);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="invite-partner-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-emerald-600" />
            Invite your partner
          </DialogTitle>
          <DialogDescription>
            Share your household finances together. Your partner will receive an email invitation.
          </DialogDescription>
        </DialogHeader>
        
        {success ? (
          <div className="py-8 text-center" data-testid="invite-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
            <p className="text-lg font-medium text-foreground">Invitation sent</p>
            <p className="text-sm text-muted-foreground mt-1">
              They will receive an email with instructions to join.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="partner-email">Partner's email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="partner-email"
                  type="email"
                  placeholder="partner@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  disabled={loading}
                  data-testid="partner-email-input"
                  autoComplete="off"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="invite-message">
                Personal message <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Textarea
                id="invite-message"
                placeholder="Add a personal note..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="resize-none"
                rows={3}
                maxLength={200}
                disabled={loading}
                data-testid="invite-message-input"
              />
              <p className="text-xs text-muted-foreground text-right">
                {message.length}/200
              </p>
            </div>
            
            <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-900">
              <Info className="w-4 h-4 text-blue-600" />
              <AlertDescription className="text-sm text-blue-800 dark:text-blue-300">
                Your partner will have full access to view and manage your shared finances. You can both track transactions, set budgets, and work toward goals together.
              </AlertDescription>
            </Alert>
            
            {error && (
              <Alert variant="destructive" data-testid="invite-error">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={loading}
                data-testid="invite-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading || !email.trim()}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="invite-send-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Send invitation
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default InvitePartnerModal;
